import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { respondApi, CustomField } from '../services/api';
import { parseISO, format } from 'date-fns';
import { DateTimeSelector, AvailabilityDay, SelectedSlot, AvailabilitySlot } from '../components/DateTimeSelector';
import { useEmbedMode } from '../hooks/useEmbedMode';
import { useTheme } from '../hooks/useTheme';
import { Clock, Check, Sun, Moon } from 'lucide-react';
import { Footer } from '../components/Footer';
import { detectTimezone } from '../utils/timezones';
import { CustomFormFields } from '../components/CustomFormFields';

interface EventData {
    id: string;
    name: string;
    description?: string;
    comments?: string;
    default_theme?: 'dark' | 'light' | 'system';
    creator_name: string;
    creator_email: string;
    duration_minutes: number;
    custom_fields?: CustomField[];
    time_slots: {
        id: string;
        start_time_utc: string;
        end_time_utc: string;
    }[];
}

export function RespondPage() {
    const { token } = useParams<{ token: string }>();
    const { isEmbedded, notifyParentHeight } = useEmbedMode();
    const [event, setEvent] = useState<EventData | null>(null);
    const [defaultTheme, setDefaultTheme] = useState<'dark' | 'light' | 'system'>('dark');
    const { toggleTheme, isDark } = useTheme({ 
        defaultTheme,
        storageKey: token 
    });
    const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());
    const [timezone, setTimezone] = useState(() => detectTimezone());
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    
    // Custom field responses
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
    
    // Date selection state
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    // Notify parent of height changes
    useEffect(() => {
        notifyParentHeight();
    }, [selectedSlotIds.size, success, notifyParentHeight]);


    // Track the most recent request to prevent race conditions
    const requestIdRef = useRef(0);
    
    useEffect(() => {
        if (!token) return;
        
        // Increment request ID to invalidate any in-flight requests
        const currentRequestId = ++requestIdRef.current;
        
        // Determine if this is initial load or timezone change
        const isInitialLoad = isLoading;
        
        // Save current selections before reloading (for timezone changes)
        const currentSelections = new Set(selectedSlotIds);
        
        const loadData = async () => {
            try {
                // Pass timezone to backend for conversion
                const data = await respondApi.getEvent(token, timezone);
                
                // Race condition check: only proceed if this is still the latest request
                if (currentRequestId !== requestIdRef.current) {
                    console.log('Discarding stale request response');
                    return;
                }
                
                setEvent(data?.event);
                
                // Handle selections based on context
                if (isInitialLoad) {
                    // Initial load: reset everything and use backend data
                    if (data?.recipient?.selected_slot_ids) {
                        setSelectedSlotIds(new Set(data.recipient.selected_slot_ids));
                    } else {
                        setSelectedSlotIds(new Set());
                    }
                } else {
                    // Timezone change: preserve local selections if not yet submitted
                    if (currentSelections.size > 0 && !success) {
                        // Keep local selections - user hasn't submitted yet
                        setSelectedSlotIds(currentSelections);
                    } else if (data?.recipient?.selected_slot_ids) {
                        // Use backend-persisted selections
                        setSelectedSlotIds(new Set(data.recipient.selected_slot_ids));
                    }
                }
                
                // Preserve custom field values
                if (data?.recipient?.custom_field_data) {
                    setCustomFieldValues(data.recipient.custom_field_data);
                }
                
                setError(null);
            } catch (err: any) {
                // Only show error if this is still the latest request
                if (currentRequestId === requestIdRef.current) {
                    setError(err.message || 'Failed to load event');
                }
            } finally {
                // Only update loading state if this is still the latest request
                if (currentRequestId === requestIdRef.current && isInitialLoad) {
                    setIsLoading(false);
                }
            }
        };
        
        loadData();
    }, [token, timezone]); // Reload on token OR timezone change, with race condition protection

    // Update theme when event data loads
    useEffect(() => {
        if (event?.default_theme) {
            setDefaultTheme(event.default_theme);
        }
    }, [event?.default_theme]);

    // Update document title with event name
    useEffect(() => {
        if (event?.name) {
            document.title = `AAM Let's Meet: ${event.name}`;
        }
        return () => {
            document.title = "AAM Let's Meet";
        };
    }, [event?.name]);

    // Note: Data loading is now handled entirely by the useEffect above
    // This prevents race conditions and ensures timezone changes are handled correctly

    // Convert event time slots to AvailabilityDay format for DateTimeSelector
    // Note: Times are already converted from creator's timezone to recipient's timezone by the backend
    // The field is named 'start_time_utc' for API compatibility, but contains local time in recipient's timezone
    const availableDays = useMemo<AvailabilityDay[]>(() => {
        if (!event) return [];

        // Group slots by date (using recipient's local timezone, already converted by backend)
        const slotsByDate: Record<string, typeof event.time_slots> = {};
        
        event.time_slots.forEach(slot => {
            const date = format(parseISO(slot.start_time_utc), 'yyyy-MM-dd');
            if (!slotsByDate[date]) slotsByDate[date] = [];
            slotsByDate[date].push(slot);
        });

        // Convert to AvailabilityDay format
        return Object.entries(slotsByDate)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, slots]) => {
                const dateObj = parseISO(date);
                const dayName = format(dateObj, 'EEEE');
                
                return {
                    date,
                    day_name: dayName,
                    formatted_date: format(dateObj, 'MMMM d, yyyy'),
                    slots: slots.map(slot => ({
                        start_time_utc: slot.start_time_utc,
                        end_time_utc: slot.end_time_utc,
                        start_time_local: format(parseISO(slot.start_time_utc), 'h:mm a'),
                        end_time_local: format(parseISO(slot.end_time_utc), 'h:mm a'),
                        // Store the slot id for selection tracking
                        id: slot.id
                    }))
                };
            });
    }, [event]);

    // Auto-select first available date
    useEffect(() => {
        if (availableDays.length > 0 && !selectedDate) {
            setSelectedDate(availableDays[0].date);
        }
    }, [availableDays, selectedDate]);

    // Convert selectedSlotIds to SelectedSlot format
    const selectedSlots = useMemo<SelectedSlot[]>(() => {
        if (!event) return [];
        
        const slots: SelectedSlot[] = [];
        availableDays.forEach(day => {
            day.slots.forEach(slot => {
                // Find the original slot by matching times
                const originalSlot = event.time_slots.find(
                    s => s.start_time_utc === slot.start_time_utc && s.end_time_utc === slot.end_time_utc
                );
                
                if (originalSlot && selectedSlotIds.has(originalSlot.id)) {
                    slots.push({
                        ...slot,
                        date: day.date,
                        day_name: day.day_name
                    });
                }
            });
        });
        
        return slots;
    }, [event, availableDays, selectedSlotIds]);

    const handleSlotToggle = (slot: AvailabilitySlot, _date: string, _dayName: string) => {
        if (!event) return;

        // Find the original slot ID
        const originalSlot = event.time_slots.find(
            s => s.start_time_utc === slot.start_time_utc && s.end_time_utc === slot.end_time_utc
        );

        if (!originalSlot) return;

        setSelectedSlotIds(prev => {
            const next = new Set(prev);
            if (next.has(originalSlot.id)) {
                next.delete(originalSlot.id);
            } else {
                next.add(originalSlot.id);
            }
            return next;
        });
        setSuccess(false);
    };

    const handleCustomFieldChange = (fieldName: string, value: any) => {
        setCustomFieldValues(prev => ({ ...prev, [fieldName]: value }));
        // Clear error when user starts typing
        if (customFieldErrors[fieldName]) {
            setCustomFieldErrors(prev => {
                const next = { ...prev };
                delete next[fieldName];
                return next;
            });
        }
    };

    const validateCustomFields = (): boolean => {
        if (!event?.custom_fields || event.custom_fields.length === 0) {
            return true;
        }

        const errors: Record<string, string> = {};
        event.custom_fields.forEach(field => {
            const value = customFieldValues[field.field_name];
            
            // Check required
            if (field.is_required && (!value || value === '')) {
                errors[field.field_name] = `${field.field_label} is required`;
            }
            
            // Type-specific validation
            if (value && value !== '') {
                if (field.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    errors[field.field_name] = 'Please enter a valid email address';
                }
                if (field.field_type === 'phone') {
                    const cleaned = value.replace(/[^0-9]/g, '');
                    if (cleaned.length < 10 || cleaned.length > 15) {
                        errors[field.field_name] = 'Please enter a valid phone number';
                    }
                }
            }
        });

        setCustomFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!token) return;

        // Validate custom fields
        if (!validateCustomFields()) {
            setError('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            await respondApi.submit(token, Array.from(selectedSlotIds), customFieldValues);
            setSuccess(true);
        } catch (err: any) {
            setError(err.message || 'Failed to submit response');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div className="spinner" />
                </div>
                {!isEmbedded && <Footer />}
            </div>
        );
    }

    if (error && !event) {
        return (
            <div className="booking-page" style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh'
            }}>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 'var(--space-5)'
                }}>
                    <div className="booking-card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <h2 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
                            Unable to Load Event
                        </h2>
                        <p style={{ color: '#888' }}>{error}</p>
                    </div>
                </div>
                {!isEmbedded && <Footer />}
            </div>
        );
    }

    if (!event) return null;

    return (
        <div className="booking-page" style={{ 
            paddingBottom: isEmbedded ? 'var(--space-4)' : 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
        }}>
            {/* Header - hidden in embed mode */}
            <div className="embed-hide" style={{
                background: 'rgba(255,255,255,0.05)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-6)'
            }}>
                <div className="container" style={{ maxWidth: '900px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={isDark ? "/logo.png" : "/light_logo.png"} alt="AAM Let's Meet" style={{ height: '32px' }} />
                        </div>
                        <button
                            onClick={toggleTheme}
                            className="theme-toggle"
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container" style={{ maxWidth: '900px', flex: 1 }}>
                {/* Event Info Card */}
                <div className="booking-card" style={{ marginBottom: 'var(--space-4)' }}>
                    <h1 style={{ margin: 0, marginBottom: 'var(--space-2)' }}>{event.name}</h1>

                    <p style={{
                        color: '#888',
                        marginBottom: 'var(--space-4)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        From <strong style={{ color: isDark ? '#fff' : '#1a1a2e' }}>{event.creator_name}</strong>
                    </p>

                    {event.description && (
                        <div style={{
                            background: isDark ? '#2a2a2a' : '#f5f5f5',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)',
                            color: isDark ? '#ccc' : '#555',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {event.description}
                        </div>
                    )}

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-4)',
                        color: '#888',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock size={16} />
                            {event.duration_minutes} minutes
                        </span>
                    </div>
                </div>

                {/* Success Message */}
                {success && (
                    <div style={{ 
                        background: 'rgba(74, 222, 128, 0.1)',
                        border: '1px solid #4ade80',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--space-4)',
                        marginBottom: 'var(--space-4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        color: '#4ade80'
                    }}>
                        <Check size={18} />
                        Your response has been recorded!
                    </div>
                )}

                {/* Instructions */}
                <div className="booking-card" style={{ marginBottom: 'var(--space-4)' }}>
                    <h3 style={{ marginBottom: 'var(--space-2)' }}>
                        Select the times that work for you
                    </h3>
                    <p style={{ color: '#888', fontSize: 'var(--font-size-sm)' }}>
                        Click on any available times that work for your schedule. You can select multiple times.
                    </p>
                </div>

                {/* Date Time Selector */}
                <DateTimeSelector
                    availableDays={availableDays}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                    selectedSlots={selectedSlots}
                    onSlotToggle={handleSlotToggle}
                    multiSelect={true}
                    timezone={timezone}
                    onTimezoneChange={setTimezone}
                    showTimezoneSelector={true}
                    ownerName={event.creator_name}
                />

                {/* Custom Form Fields */}
                {event.custom_fields && event.custom_fields.length > 0 && (
                    <div className="booking-card" style={{ marginTop: 'var(--space-4)' }}>
                        <h3 style={{ marginBottom: 'var(--space-4)' }}>Additional Information</h3>
                        <CustomFormFields
                            fields={event.custom_fields as any}
                            values={customFieldValues}
                            onChange={handleCustomFieldChange}
                            errors={customFieldErrors}
                        />
                    </div>
                )}

                {/* Submit Section */}
                <div className="booking-card" style={{ marginTop: 'var(--space-4)' }}>
                    {error && (
                        <div style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid #ef4444',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--space-3)',
                            marginBottom: 'var(--space-3)',
                            color: '#ef4444',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            {error}
                        </div>
                    )}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 'var(--space-3)'
                    }}>
                        <div>
                            <p style={{ color: '#888', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                                {selectedSlotIds.size} time{selectedSlotIds.size !== 1 ? 's' : ''} selected
                            </p>
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="btn btn-primary btn-lg"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="spinner" style={{ width: '18px', height: '18px' }} />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Check size={18} />
                                    Submit Response
                                </>
                            )}
                        </button>
                    </div>
                    
                    <p style={{
                        textAlign: 'center',
                        marginTop: 'var(--space-4)',
                        fontSize: 'var(--font-size-sm)',
                        color: '#666'
                    }}>
                        You can return to this page and update your selections anytime
                    </p>
                </div>
            </div>

            {!isEmbedded && <Footer />}
        </div>
    );
}
